import { useState, useEffect } from 'react'
import {
    Box, Card, CardContent, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, Avatar,
    TextField, InputAdornment, LinearProgress
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'
import { formatDate } from '../../utils'
import type { User } from '../../types'

export default function UsersPage() {
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')

    const db = getFirebaseDb()

    useEffect(() => {
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
        const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
            const userData = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() })) as User[]
            setUsers(userData)
            setLoading(false)
        })
        return () => unsubscribe()
    }, [db])

    const filteredUsers = users.filter(u =>
        u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Users ({filteredUsers.length})</Typography>

            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 2 }}>
                    <TextField
                        fullWidth placeholder="Search users..." value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'grey.500' }} /></InputAdornment> }}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    {loading ? <LinearProgress /> : filteredUsers.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 4 }}>No users found</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Joined</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.uid} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.main' }}>{user.displayName?.charAt(0) || 'U'}</Avatar>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{user.displayName || 'Unknown'}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip label={user.role} size="small" color={user.role === 'admin' ? 'primary' : user.role === 'delivery_boy' ? 'secondary' : 'default'} />
                                            </TableCell>
                                            <TableCell>{user.phone || 'N/A'}</TableCell>
                                            <TableCell>{formatDate(user.createdAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}
